import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { validatePassword, PASSWORD_HINT_NEW } from "../passwordPolicy";
import LegalFooter from "../components/LegalFooter";
import usePlayerManifest from "../hooks/usePlayerManifest";
import PasswordInput from "../components/PasswordInput";

export default function PlayerRegister() {
  usePlayerManifest();
  const [form, setForm] = useState({ society_id: "", first_name: "", last_name: "", email: "", password: "", confirmPassword: "" });
  const [societies, setSocieties] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A link can pre-specify the society (?society_id=7) so an invited member
  // never has to pick from a list of every society on GSM -- removes any
  // risk of picking the wrong one when similarly-named societies exist.
  const presetSocietyId = searchParams.get("society_id");

  useEffect(() => {
    fetch("/api/verify/societies")
      .then(r => r.json())
      .then(data => {
        setSocieties(Array.isArray(data) ? data : []);
        if (presetSocietyId) setForm(f => ({ ...f, society_id: presetSocietyId }));
      })
      .catch(() => setError("Could not load societies"));
  }, [presetSocietyId]);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async () => {
    setError("");
    if (!form.society_id || !form.first_name || !form.last_name || !form.email || !form.password)
      return setError("All fields are required");
    if (form.password !== form.confirmPassword)
      return setError("Passwords do not match");
    const passwordError = validatePassword(form.password, 12);
    if (passwordError) return setError(passwordError);
    setLoading(true);
    try {
      const res = await fetch("/api/player/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ society_id: parseInt(form.society_id), first_name: form.first_name, last_name: form.last_name, email: form.email, password: form.password })
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || "Registration failed");
      sessionStorage.setItem("player_token", data.token);
      sessionStorage.setItem("player_name", form.first_name + " " + form.last_name);
      navigate(data.linked ? "/player/dashboard" : "/player/dashboard?notice=unlinked");
    } catch(e) { setError("Network error"); }
    finally { setLoading(false); }
  };

  const inp = (label, name, type = "text", placeholder = "") => {
    const fieldStyle = { width: "100%", border: "1.5px solid #d1d5db", borderRadius: "8px", padding: "11px 14px", fontSize: "15px", boxSizing: "border-box" };
    return (
      <div>
        <label style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "#374151", marginBottom: "6px" }}>{label}</label>
        {type === "password" ? (
          <PasswordInput name={name} value={form[name]} onChange={handle} placeholder={placeholder}
            onKeyDown={e => e.key === "Enter" && submit()} style={fieldStyle} />
        ) : (
          <input name={name} type={type} value={form[name]} onChange={handle} placeholder={placeholder}
            onKeyDown={e => e.key === "Enter" && submit()} style={fieldStyle} />
        )}
      </div>
    );
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
      <div style={{ background: "white", padding: "32px", borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.10)", width: "100%", maxWidth: "420px" }}>

        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <div style={{ width: "64px", height: "64px", background: "#14532d", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", fontSize: "28px" }}>⛳</div>
          <h1 style={{ margin: 0, fontSize: "22px", fontWeight: "800", color: "#14532d" }}>Golf Society Manager</h1>
          <p style={{ margin: "6px 0 0", fontSize: "14px", color: "#6b7280" }}>Player Registration</p>
        </div>

        {error && <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "10px 14px", borderRadius: "8px", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}

        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          {presetSocietyId ? (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "11px 14px", fontSize: "15px", color: "#14532d", fontWeight: "600" }}>
              ⛳ {societies.find(s => String(s.id) === String(presetSocietyId))?.name || "Loading…"}
            </div>
          ) : (
            <div>
              <label style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "#374151", marginBottom: "6px" }}>Golf Society</label>
              <select name="society_id" value={form.society_id} onChange={handle}
                style={{ width: "100%", border: "1.5px solid #d1d5db", borderRadius: "8px", padding: "11px 14px", fontSize: "15px", boxSizing: "border-box", background: "white" }}>
                <option value="">— Select your society —</option>
                {societies.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}

          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "#374151", marginBottom: "6px" }}>Surname</label>
              <input name="last_name" value={form.last_name} onChange={handle} placeholder="Sellman"
                style={{ width: "100%", border: "1.5px solid #d1d5db", borderRadius: "8px", padding: "11px 14px", fontSize: "15px", boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "#374151", marginBottom: "6px" }}>First Name</label>
              <input name="first_name" value={form.first_name} onChange={handle} placeholder="Michael"
                style={{ width: "100%", border: "1.5px solid #d1d5db", borderRadius: "8px", padding: "11px 14px", fontSize: "15px", boxSizing: "border-box" }} />
            </div>
          </div>

          {inp("Email Address", "email", "email", "your@email.com")}
          {inp("Password", "password", "password", "Choose a password")}
          <p style={{ fontSize: "13px", fontWeight: "600", color: "#4b5563", marginTop: "-10px" }}>{PASSWORD_HINT_NEW}</p>
          {inp("Confirm Password", "confirmPassword", "password", "Repeat your password")}

          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "10px 12px", fontSize: "12px", color: "#166534" }}>
            Your name must match exactly what your society organiser has on record. Names are stored as Surname Firstname — e.g. register as First: Michael, Last: Sellman if your organiser has you as "Sellman Michael".
          </div>

          <button onClick={submit} disabled={loading}
            style={{ width: "100%", background: loading ? "#86efac" : "#166534", color: "white", fontWeight: "700", fontSize: "16px", padding: "13px", borderRadius: "8px", border: "none", cursor: loading ? "not-allowed" : "pointer" }}>
            {loading ? "Registering..." : "Create Player Account"}
          </button>

          <button onClick={() => navigate("/login")}
            style={{ background: "none", border: "none", fontSize: "14px", color: "#166534", fontWeight: "600", cursor: "pointer" }}>
            Already registered? Sign in
          </button>
        </div>
        <div style={{ marginTop: "20px" }}>
          <LegalFooter />
        </div>
      </div>
    </div>
  );
}
